import { useMemo } from "react";
import { setSnack } from "@synthesis/shared-ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CursorPageDtoNotificationDto, DeleteByScopeScopeKey } from "@synthesis/shared-services/notification/external/types";

import { useClientType } from "../../shared/hooks/use-client-type";
import { useUserInfo } from "../../shared/hooks/use-user-info";
import { mapNotifications } from "./map-notification.dto";
import {
  getDeleteByScopeFn,
  getDeleteOneFn,
  getMarkAllReadFn,
  getMarkReadFn,
  getNotificationListQueryKey,
  getNotificationSettings,
  getNotificationSettingsQueryKey,
  getNotifications,
  getNotificationUnreadCountQueryKey,
  getUnreadNotificationsCount,
  getUpdateSettingsFn,
} from "./notifications-api.lib";
import { adaptNotification } from "./notifications.adapters";
import {
  NOTIFICATIONS_PAGE_SIZE,
  type NotificationClientType,
  type NotificationDeleteType,
  type NotificationViewModel,
} from "./notifications-panel.lib";

type UseApplicationNotificationsResult = {
  clientType: NotificationClientType | null;
  deleteNotification: (notificationId: string) => void;
  deleteNotificationsByType: (type: NotificationDeleteType) => void;
  email: { isChecked: boolean };
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isDeleting: boolean;
  isEmailFetching: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  isSettingEmail: boolean;
  isViewing: boolean;
  markAllNotificationsAsRead: () => void;
  markNotificationAsRead: (notificationId: string) => void;
  markNotificationAsReadAsync: (notificationId: string) => Promise<void>;
  notifications: NotificationViewModel[];
  refetchNewNotificationsCount: () => void;
  refetchNotifications: () => void;
  setEmailChecked: (isSend: boolean) => void;
  totalCount: number;
  unreadCount: number;
};

export const getNotificationsQueryKey = getNotificationListQueryKey;

export const getNewNotificationsCountQueryKey = getNotificationUnreadCountQueryKey;

const DELETE_TYPE_TO_SCOPE: Record<Exclude<NotificationDeleteType, "single">, DeleteByScopeScopeKey> = {
  all: "ALL",
  read: "READ",
  unread: "UNREAD",
};

const CONTOUR_ERROR_MESSAGE = "Не удалось определить контур уведомлений";

const notifyMutationError = (description: string) => (error: Error) => {
  setSnack({
    title: "Произошла ошибка",
    description: error.message || description,
    messageType: "error",
  });
};

const getNotificationsNextPageParam = (lastPage: CursorPageDtoNotificationDto): string | undefined =>
  lastPage.hasNext ? (lastPage.nextCursor ?? undefined) : undefined;

const useNotificationsListQuery = (clientType: NotificationClientType | null, userId: string | undefined) =>
  useInfiniteQuery({
    enabled: Boolean(clientType && userId),
    getNextPageParam: getNotificationsNextPageParam,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) => getNotifications(clientType!, { cursor: pageParam, size: NOTIFICATIONS_PAGE_SIZE }, { signal }),
    queryKey: getNotificationListQueryKey(clientType, userId),
  });

const useUnreadCountQuery = (clientType: NotificationClientType | null, userId: string | undefined) =>
  useQuery({
    enabled: Boolean(clientType && userId),
    queryFn: ({ signal }) => getUnreadNotificationsCount(clientType!, { signal }),
    queryKey: getNotificationUnreadCountQueryKey(clientType, userId),
  });

const useNotificationSettingsQuery = (clientType: NotificationClientType | null, userId: string | undefined) =>
  useQuery({
    enabled: Boolean(clientType && userId),
    queryFn: ({ signal }) => getNotificationSettings(clientType!, { signal }),
    queryKey: getNotificationSettingsQueryKey(clientType, userId),
  });

export const useNotificationsUnreadCount = () => {
  const clientType = useClientType();
  const userId = useUserInfo()?.id;
  const unreadCountQuery = useUnreadCountQuery(clientType, userId);

  return { count: unreadCountQuery.data?.count ?? 0, refetch: unreadCountQuery.refetch };
};

export const useApplicationNotifications = (): UseApplicationNotificationsResult => {
  const clientType = useClientType();
  const userInfo = useUserInfo();
  const queryClient = useQueryClient();
  const userId = userInfo?.id;

  const listQuery = useNotificationsListQuery(clientType, userId);
  const unreadCountQuery = useUnreadCountQuery(clientType, userId);
  const settingsQuery = useNotificationSettingsQuery(clientType, userId);

  const invalidateNotifications = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getNotificationListQueryKey(clientType, userId) }),
      queryClient.invalidateQueries({ queryKey: getNotificationUnreadCountQueryKey(clientType, userId) }),
    ]);
  };

  const markReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      if (!clientType) {
        throw new Error(CONTOUR_ERROR_MESSAGE);
      }

      await getMarkReadFn(clientType)({ path: { notificationId } });
    },
    onError: notifyMutationError("Произошла ошибка при отметке уведомления как прочитанного"),
    onSuccess: invalidateNotifications,
  });

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      if (!clientType) {
        throw new Error(CONTOUR_ERROR_MESSAGE);
      }

      await getMarkAllReadFn(clientType)();
    },
    onError: notifyMutationError("Произошла ошибка при отметке всех уведомлений как прочитанных"),
    onSuccess: invalidateNotifications,
  });

  const deleteOneMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      if (!clientType) {
        throw new Error(CONTOUR_ERROR_MESSAGE);
      }

      await getDeleteOneFn(clientType)({ path: { notificationId } });
    },
    onError: notifyMutationError("Произошла ошибка при удалении уведомления"),
    onSuccess: invalidateNotifications,
  });

  const deleteByScopeMutation = useMutation({
    mutationFn: async (type: Exclude<NotificationDeleteType, "single">) => {
      if (!clientType) {
        throw new Error(CONTOUR_ERROR_MESSAGE);
      }

      await getDeleteByScopeFn(clientType)(DELETE_TYPE_TO_SCOPE[type]);
    },
    onError: notifyMutationError("Произошла ошибка при удалении уведомлений"),
    onSuccess: invalidateNotifications,
  });

  const updateSettingsMutation = useMutation({
    mutationFn: async (emailEnabled: boolean) => {
      if (!clientType) {
        throw new Error(CONTOUR_ERROR_MESSAGE);
      }

      await getUpdateSettingsFn(clientType)({ body: { emailEnabled } });
    },
    onError: notifyMutationError("Произошла ошибка при изменении настройки email-уведомлений"),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: getNotificationSettingsQueryKey(clientType, userId) });
    },
  });

  const rawNotifications = useMemo(
    () => mapNotifications(listQuery.data?.pages.flatMap((page) => page.content ?? []) ?? []),
    [listQuery.data?.pages],
  );
  const notifications = useMemo(() => rawNotifications.map(adaptNotification), [rawNotifications]);

  const markNotificationAsReadAsync = async (notificationId: string) => {
    if (!clientType || !userId) {
      return;
    }

    await markReadMutation.mutateAsync(notificationId);
  };

  const markNotificationAsRead = (notificationId: string) => {
    void markNotificationAsReadAsync(notificationId);
  };

  const markAllNotificationsAsRead = () => {
    void markAllReadMutation.mutateAsync();
  };

  const deleteNotification = (notificationId: string) => {
    void deleteOneMutation.mutateAsync(notificationId);
  };

  const deleteNotificationsByType = (type: NotificationDeleteType) => {
    if (type === "single") {
      return;
    }

    void deleteByScopeMutation.mutateAsync(type);
  };

  const setEmailChecked = (isSend: boolean) => {
    void updateSettingsMutation.mutateAsync(isSend);
  };

  const fetchNextPage = () => {
    void listQuery.fetchNextPage();
  };

  return {
    clientType,
    deleteNotification,
    deleteNotificationsByType,
    email: { isChecked: settingsQuery.data?.emailEnabled ?? false },
    fetchNextPage,
    hasNextPage: listQuery.hasNextPage,
    isDeleting: deleteOneMutation.isPending || deleteByScopeMutation.isPending,
    isEmailFetching: settingsQuery.isFetching,
    isFetchingNextPage: listQuery.isFetchingNextPage,
    isLoading: listQuery.isLoading,
    isSettingEmail: updateSettingsMutation.isPending,
    isViewing: markReadMutation.isPending || markAllReadMutation.isPending,
    markAllNotificationsAsRead,
    markNotificationAsRead,
    markNotificationAsReadAsync,
    notifications,
    refetchNewNotificationsCount: unreadCountQuery.refetch,
    refetchNotifications: listQuery.refetch,
    setEmailChecked,
    totalCount: notifications.length,
    unreadCount: unreadCountQuery.data?.count ?? 0,
  };
};
